import { Check, Quote, Star } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClosingCta, GhostCta, PrimaryCta } from "./Cta";
import { Lottie } from "./Lottie";
import { IsoScreen } from "./IsoScreen";
import { FEATURE_SCREENS } from "@/content/screens";
import { FEATURE_LOTTIE, LOTTIE } from "@/content/animations";
import { FEATURE_ISO, FEATURE_ROW_ISO, ISO, ISO_ALT, ISO_CAPTION } from "@/content/illustrations";
import { IsoGrid, IsoIllustration, IsoPlate, IsoStep } from "./Iso";
import { useT, useTx } from "@/lib/i18n-dict";
import { Reveal } from "./Reveal";
import { Section, SectionHeading, ZigzagRow } from "./Section";
import { LocaleLink } from "./LocaleLink";
import { RelatedLinks } from "./RelatedLinks";
import type { FeaturePageContent } from "@/content/features";
import { FEATURE_EXTRAS } from "@/content/features.extra";
import { FEATURE_EXTRAS_2 } from "@/content/features.extra2";


import modCost from "@/assets/modules/cost.svg";
import modPlanning from "@/assets/modules/planning.svg";
import modProduction from "@/assets/modules/production.svg";
import modPurchases from "@/assets/modules/purchases.svg";
import modSales from "@/assets/modules/sales.svg";
import modScheduling from "@/assets/modules/scheduling.svg";
import modStocks from "@/assets/modules/stocks.svg";
import modTechnicalData from "@/assets/modules/technical-data.svg";

/** Schematic module artwork used for the first zigzag row (thematic, not a fake screenshot). */
const MODULE_SVG: Record<string, string> = {
  sales: modSales,
  production: modProduction,
  purchases: modPurchases,
  stocks: modStocks,
  planning: modPlanning,
  scheduling: modScheduling,
  cost: modCost,
  "technical-data": modTechnicalData,
};

function Visual({
  alt,
  caption,
  svg,
}: {
  alt: string;
  caption?: string | undefined;
  svg: string;
}) {
  return (
    <IsoPlate className="p-3">
      <figure className="m-0">
        <img
          src={svg}
          alt={alt}
          loading="lazy"
          className="w-full rounded-xl"
        />
        {caption ? (
          <figcaption className="mt-3 px-1 text-center text-sm leading-relaxed text-muted-foreground">
            {caption}
          </figcaption>
        ) : null}
      </figure>
    </IsoPlate>
  );
}

/** Alt text and caption for the traced module artwork, per module slug. */
const MODULE_META: Record<string, { alt: string; caption: string }> = {
  sales: {
    alt: "Schematic of the Sales module: quotes, orders and dispatch documents on an isometric desk",
    caption: "Sales workspace: quote → order → dispatch",
  },
  production: {
    alt: "Schematic of the Production module: machines, job orders and output on an isometric shop floor",
    caption: "Shop-floor view of production execution",
  },
  purchases: {
    alt: "Schematic of the Purchases module: supplier orders and receipts on an isometric desk",
    caption: "Purchasing from requisition to receipt",
  },
  stocks: {
    alt: "Schematic of the Stocks module: warehouse racks, bins and stock movements",
    caption: "Warehouse stock under full control",
  },
  planning: {
    alt: "Schematic of the Planning module: demand, forecast and supply on an isometric planning board",
    caption: "One plan for demand and supply",
  },
  scheduling: {
    alt: "Schematic of the Scheduling module: operations on an isometric Gantt timeline",
    caption: "Finite scheduling across work centres",
  },
  cost: {
    alt: "Schematic of the Cost module: cost components rolling up into product cost",
    caption: "Actual product cost, rolled up live",
  },
  "technical-data": {
    alt: "Schematic of the Technical Data module: BOM, routing and revisions linked together",
    caption: "BOM & routing versions in sync",
  },
};

interface RowVisual {
  svg: string;
  alt: string;
  caption: string | undefined;
}

/** Thematic SVG for a zigzag row: hand-authored scene first, traced module art for row 0. */
function rowVisual(slug: string, index: number): RowVisual {
  const key = FEATURE_ROW_ISO[slug]?.[index];
  if (key) {
    return { svg: ISO[key], alt: ISO_ALT[key], caption: ISO_CAPTION[key] };
  }
  const moduleMeta = MODULE_META[slug];
  if (MODULE_SVG[slug] && moduleMeta) {
    return { svg: MODULE_SVG[slug], alt: moduleMeta.alt, caption: moduleMeta.caption };
  }
  const heroKey = FEATURE_ISO[slug] ?? "smartFactory";
  return { svg: ISO[heroKey], alt: ISO_ALT[heroKey], caption: ISO_CAPTION[heroKey] };
}

export function FeaturePage({ content: source }: { content: FeaturePageContent }) {
  const t = useT();
  const content = useTx(source);
  const heroLottie = FEATURE_LOTTIE[content.slug];
  const heroIso = FEATURE_ISO[content.slug];
  const heroScreen = FEATURE_SCREENS[content.slug];
  const extra = useTx(FEATURE_EXTRAS[content.slug] ?? FEATURE_EXTRAS_2[content.slug]);

  // Skip zigzag rows that repeat blocks already rendered from the richer data.
  const zigzagSections = extra
    ? content.sections.filter((section) => {
        const title = section.title.toLowerCase();
        return (
          !title.includes("in the box") &&
          title !== extra.stepsTitle.toLowerCase()
        );
      })
    : content.sections;
  return (
    <>
      <header className="hero-glow relative isolate border-b border-border/60 py-20 md:py-28">
        <IsoGrid intensity="medium" />
        <div className="container-page grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">

          <Reveal>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {content.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl font-bold text-balance-tight md:text-5xl">
              {content.title}
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">{content.lead}</p>
            <ul className="mt-8 grid gap-3">
              {content.heroBullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-sm text-muted-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  {bullet}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
              <GhostCta to="/contacts">Talk to a consultant</GhostCta>
            </div>
          </Reveal>
          <Reveal delay={100}>
            {heroScreen ? (
              <div className="relative">
                <div
                  className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-primary/10 blur-3xl"
                  aria-hidden
                />
                <IsoScreen screen={heroScreen} caption />
              </div>
            ) : heroLottie ? (
              <div className="relative">
                <div
                  className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-primary/10 blur-3xl"
                  aria-hidden
                />
                <Lottie
                  src={LOTTIE[heroLottie]}
                  className="mx-auto aspect-square w-full max-w-[520px]"
                  ariaLabel={`${content.eyebrow} illustration`}
                />
              </div>
            ) : (
              <Visual
                svg={rowVisual(content.slug, -1).svg}
                alt={t(rowVisual(content.slug, -1).alt)}
                caption={
                  rowVisual(content.slug, -1).caption
                    ? t(rowVisual(content.slug, -1).caption as string)
                    : undefined
                }
              />
            )}
          </Reveal>

        </div>
      </header>

      {extra?.metrics.length ? (
        <section className="relative isolate border-b border-border/60 bg-surface/40 py-12">
          <IsoGrid intensity="soft" />
          <div className="container-page grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {extra.metrics.map((metric, index) => (
              <Reveal
                key={metric.label}
                variant="iso"
                delay={index * 60}
                className="relative overflow-hidden rounded-2xl border border-border bg-card p-6"
              >
                <span
                  aria-hidden
                  className="absolute -top-6 -right-6 size-20 skew-y-[-20deg] border border-primary/25 bg-primary/5"
                />
                <p className="relative font-display text-4xl font-bold text-primary">
                  {metric.value}
                </p>
                <p className="relative mt-2 text-sm text-muted-foreground">{metric.label}</p>
              </Reveal>
            ))}
          </div>
        </section>
      ) : null}


      {extra?.steps.length ? (
        <Section>
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.85fr]">
            <div>
              <SectionHeading
                eyebrow={t("How it works")}
                title={extra.stepsTitle}
                lead={extra.stepsLead}
              />
            </div>
            {heroIso ? (
              <Reveal variant="iso" delay={80}>
                <IsoIllustration
                  name={heroIso}
                  alt={t(ISO_ALT[heroIso])}
                />
              </Reveal>
            ) : null}
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {extra.steps.map((step, index) => (
              <Reveal
                key={step.title}
                variant="iso"
                delay={index * 70}
                className="group relative rounded-2xl border border-border bg-card p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_24px_60px_-40px_oklch(0_0_0/85%)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <IsoStep n={String(index + 1).padStart(2, "0")} />

                <h3 className="mt-3 font-display text-lg font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.text}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {zigzagSections.length ? (
        <Section className={extra ? "bg-surface/40" : ""}>
          <div className="divide-y divide-border/60">
            {zigzagSections.map((section, index) => {
              const visual = rowVisual(content.slug, index);
              return (
                <ZigzagRow
                  key={section.title}
                  index={index}
                  eyebrow={String(index + 1).padStart(2, "0")}
                  kicker={section.kicker}
                  title={section.title}
                  body={section.body}
                  items={section.bullets ?? []}
                  visual={
                    index === 0 && heroScreen && heroLottie ? (
                      <IsoPlate className="p-3">
                        <Lottie
                          src={LOTTIE[heroLottie]}
                          className="mx-auto aspect-square w-full max-w-[460px]"
                          ariaLabel={`${content.eyebrow} illustration`}
                        />
                      </IsoPlate>
                    ) : (
                      <Visual
                        alt={t(visual.alt)}
                        caption={visual.caption ? t(visual.caption) : undefined}
                        svg={visual.svg}
                      />
                    )
                  }

                />
              );
            })}
          </div>
        </Section>
      ) : null}


      {extra?.roles.length ? (
        <Section>
          <SectionHeading eyebrow={t("Roles")} title={t("What will you gain?")} />
          <Reveal className="mt-10">
            <Tabs defaultValue={extra.roles[0]?.role ?? "role"} className="w-full">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                {extra.roles.map((tab) => (
                  <TabsTrigger
                    key={tab.role}
                    value={tab.role}
                    className="rounded-full border border-border px-4 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {t(tab.role)}
                  </TabsTrigger>
                ))}
              </TabsList>
              {extra.roles.map((tab) => (
                <TabsContent key={tab.role} value={tab.role} className="mt-8">
                  <div className="grid gap-6 md:grid-cols-3">
                    {tab.items.map((item) => (
                      <div
                        key={item.title}
                        className="rounded-2xl border border-border bg-card p-6"
                      >
                        <h3 className="font-display text-lg font-semibold">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </Reveal>
        </Section>
      ) : null}

      {extra?.included.length ? (
        <Section className="bg-surface/40">
          <SectionHeading eyebrow={t("Scope")} title={t("What's in the box?")} />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {extra.included.map((item, index) => (
              <Reveal
                key={item}
                delay={index * 40}
                className="flex gap-3 rounded-xl border border-border bg-card p-4"
              >
                <Check className="mt-1 size-4 shrink-0 text-primary" aria-hidden />
                <p className="text-base text-foreground/90">{item}</p>
              </Reveal>
            ))}
          </div>
          {extra.capabilities?.length ? (
            <div className="mt-12 border-t border-border/60 pt-10">
              <SectionHeading
                eyebrow={t("Built for real production")}
                title={t("More control where manufacturers feel the pain")}
                lead={t("Practical capabilities that remove rework, hidden delays and spreadsheet handoffs from daily operations.")}
              />
              <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {extra.capabilities.map((capability, index) => (
                  <Reveal
                    key={capability.title}
                    delay={index * 40}
                    className="flex h-full flex-col border-l-2 border-primary bg-card p-5"
                  >
                    <h3 className="font-display text-lg font-semibold">{capability.title}</h3>
                    <p className="mt-2 flex-1 text-base leading-relaxed text-muted-foreground">
                      {capability.text}
                    </p>
                    <LocaleLink
                      to="/prices#comparison"
                      className="mt-4 w-fit text-sm font-semibold text-primary hover:underline"
                    >
                      {t("Included from")} {capability.plan}
                    </LocaleLink>
                  </Reveal>
                ))}
              </div>
            </div>
          ) : null}
        </Section>
      ) : null}

      {content.benefits.length ? (
        <Section>
          <SectionHeading
            eyebrow={t("Why it matters")}
            title={`${t("What your team gets")}: ${content.eyebrow}`}
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {content.benefits.map((benefit, index) => (
              <Reveal
                key={benefit.title}
                delay={index * 60}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <h3 className="font-display text-lg font-semibold">{benefit.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{benefit.text}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {extra?.testimonials.length ? (
        <Section className="bg-surface/40">
          <SectionHeading eyebrow={t("Customers")} title={extra.testimonialsTitle} />
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {extra.testimonials.map((item, index) => (
              <Reveal
                key={item.name}
                delay={index * 60}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex gap-1 text-primary">
                  {Array.from({ length: 5 }).map((_, starIndex) => (
                    <Star key={starIndex} className="size-4 fill-current" aria-hidden />
                  ))}
                </div>
                <Quote className="mt-4 size-5 text-primary/50" aria-hidden />
                <p className="mt-3 text-sm text-muted-foreground">{item.quote}</p>
                <p className="mt-4 text-sm font-semibold">{item.name}</p>
                <p className="text-sm text-muted-foreground">{item.role}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {content.faq.length ? (
        <Section>
          <SectionHeading eyebrow={t("FAQ")} title={t("Frequently asked")} />
          <Reveal className="mt-10 max-w-3xl">
            <Accordion type="single" collapsible className="w-full">
              {content.faq.map((faq, index) => (
                <AccordionItem key={faq.q} value={`faq-${index}`}>
                  <AccordionTrigger className="text-start font-display text-base">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Section>
      ) : null}

      <RelatedLinks variant="feature" currentPath={`/${content.slug}`} />

      {extra?.closingTitle ? <ClosingCta title={extra.closingTitle} /> : <ClosingCta />}
    </>
  );
}

